/**
 * Organizational units as dropdown options in org-chart order: each unit is
 * followed by its sub-units, and `depth` says how far to indent it.
 *
 * Siblings sort like the org chart (by `order`, then Arabic name). A unit whose
 * parent is not in the list is shown at the top level, so a partial list still
 * shows every unit.
 */

export interface UnitTreeNode {
  id: number;
  parent?: number | null;
  order?: number;
}

export interface UnitTreeOption {
  value: string;
  label: string;
  depth: number;
}

const arabicCollator = new Intl.Collator('ar');

export function unitTreeOptions<T extends UnitTreeNode>(
  units: readonly T[],
  labelOf: (unit: T) => string,
): UnitTreeOption[] {
  const ids = new Set(units.map(u => u.id));
  const childrenOf = new Map<number | null, T[]>();
  for (const unit of units) {
    const parentKey = unit.parent != null && ids.has(unit.parent) ? unit.parent : null;
    const siblings = childrenOf.get(parentKey);
    if (siblings) siblings.push(unit);
    else childrenOf.set(parentKey, [unit]);
  }

  const bySiblingOrder = (a: T, b: T) =>
    (a.order ?? 0) - (b.order ?? 0) || arabicCollator.compare(labelOf(a), labelOf(b));

  const options: UnitTreeOption[] = [];
  const listed = new Set<number>();
  const addChildren = (parentKey: number | null, depth: number) => {
    const children = [...(childrenOf.get(parentKey) ?? [])].sort(bySiblingOrder);
    for (const unit of children) {
      if (listed.has(unit.id)) continue;
      listed.add(unit.id);
      options.push({ value: String(unit.id), label: labelOf(unit), depth });
      addChildren(unit.id, depth + 1);
    }
  };
  addChildren(null, 0);

  // Units in a parent cycle never hang off a top-level unit: list them flat.
  for (const unit of units) {
    if (!listed.has(unit.id)) {
      listed.add(unit.id);
      options.push({ value: String(unit.id), label: labelOf(unit), depth: 0 });
    }
  }
  return options;
}
